import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Package,
    Settings,
    Camera,
    Upload,
    Save,
    X,
    Trash2,
    FileText,
    AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/utils';
import api from '../../utils/api';
import { Asset, RepairRecord } from '../../types/asset';
import { User } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const EditRepair: React.FC = () => {
    const { id, repairId } = useParams<{ id: string, repairId: string }>();
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [asset, setAsset] = useState<Asset | null>(null);
    const [repair, setRepair] = useState<RepairRecord | null>(null);
    const [approvers, setApprovers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        partsRepaired: '',
        description: '',
        vendor: '',
        repairDate: '',
        cost: '',
        approvedBy: '',
        status: ''
    });

    const [files, setFiles] = useState<{ images: File[] }>({
        images: []
    });

    const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
    // Non-null opens the remove-attachment confirmation dialog
    const [attachmentToDelete, setAttachmentToDelete] = useState<number | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, repairRes, approversRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<RepairRecord>(`/api/assets/${id}/repair/${repairId}`),
                    api.get<{ users: User[] }>('/api/assets/repair-approvers')
                ]);
                
                setAsset(assetRes);
                setRepair(repairRes);
                setExistingAttachments(repairRes.attachments || []);
                
                setFormData({
                    partsRepaired: repairRes.partsRepaired || '',
                    description: repairRes.description || '',
                    vendor: repairRes.vendor || '',
                    repairDate: repairRes.repairDate ? new Date(repairRes.repairDate).toISOString().split('T')[0] : '',
                    cost: repairRes.cost ? repairRes.cost.toString() : '',
                    approvedBy: repairRes.approvedBy?.id ? repairRes.approvedBy.id.toString() : '',
                    status: repairRes.status || ''
                });

                if (Array.isArray(approversRes)) {
                    setApprovers(approversRes);
                } else if (approversRes && approversRes.users) {
                    setApprovers(approversRes.users);
                } else if (approversRes && (approversRes as any).approvers) {
                    setApprovers((approversRes as any).approvers);
                } else {
                    setApprovers([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load repair details');
                navigate(`/assets/${id}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, repairId, navigate]);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_REPAIR)) {
        return <AccessRestricted />;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            if (files.images.length + existingAttachments.length + newFiles.length > 10) {
                toast.error('Maximum of 10 attachments allowed');
                return;
            }
            setFiles(prev => ({
                ...prev,
                images: [...prev.images, ...newFiles]
            }));
        }
    };

    const removeNewImage = (indexToRemove: number) => {
        setFiles(prev => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleRemoveExistingAttachment = (attachmentId: number) => {
        setAttachmentToDelete(attachmentId);
    };

    const confirmRemoveAttachment = async () => {
        if (attachmentToDelete === null) return;
        try {
            await api.delete(`/api/assets/${id}/repair/${repairId}/attachments/${attachmentToDelete}`);
            setExistingAttachments(prev => prev.filter(att => att.id !== attachmentToDelete));
            toast.success('Attachment deleted');
        } catch (error) {
            console.error('Error deleting attachment:', error);
            toast.error('Failed to delete attachment');
        } finally {
            setAttachmentToDelete(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.partsRepaired) {
            toast.error('Please enter Parts Repaired');
            return;
        }
        if (!formData.description) {
            toast.error('Please enter a Description of Repair');
            return;
        }
        if (!formData.vendor) {
            toast.error('Please enter a Vendor');
            return;
        }
        if (!formData.repairDate) {
            toast.error('Please enter a Repair Date');
            return;
        }
        if (!formData.cost) {
            toast.error('Please enter Repair Cost');
            return;
        }
        if (!formData.approvedBy) {
            toast.error('Please select an Approver');
            return;
        }

        try {
            setSubmitting(true);
            
            // 1. Update basic info
            await api.patch(`/api/assets/${id}/repair/${repairId}`, formData);

            // 2. Upload new attachments if any
            if (files.images.length > 0) {
                const data = new FormData();
                files.images.forEach(file => {
                    data.append('attachments', file);
                });
                await api.post(`/api/assets/${id}/repair/${repairId}/attachments`, data);
            }

            toast.success('Repair record updated successfully');
            navigate(`/assets/${id}`);
        } catch (error) {
            console.error('Error saving repair record:', error);
            toast.error('Failed to update repair record');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !asset || !repair) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-8">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                    onClick={() => navigate(`/assets/${id}`)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Repair Record</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        Modify repair record for {asset.name} ({asset.tag})
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col xl:flex-row gap-6">
                {/* Main Content Area */}
                <div className="flex-1 space-y-6">
                    {/* Asset Information block */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <h2 className="text-base font-bold text-gray-900 mb-6 font-poppins">Asset Information</h2>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100 shadow-sm shrink-0 overflow-hidden">
                                {asset.image ? (
                                    <img src={asset.image} alt={asset.name} className="w-full h-full object-cover" />
                                ) : (
                                    <Package size={24} />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 leading-tight">{asset.name}</h3>
                                <div className="text-sm font-medium text-gray-500 mt-1 mb-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                    <span>{asset.tag}</span>
                                    <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-gray-300"></span>
                                    <span>{asset.type}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg border-gray-100 text-[10px] font-bold uppercase tracking-wider bg-gray-50">
                                        {asset.status}
                                    </Badge>
                                    <span className="text-xs font-medium text-gray-500">{asset.brand} {asset.model}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Repair Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <Settings className="text-gray-400" size={20} />
                                <div>
                                    <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Details</h2>
                                    <p className="text-xs font-medium text-gray-400 mt-0.5">Provide details about the repair work performed</p>
                                </div>
                            </div>
                            <Badge className={cn("px-2 py-1 rounded-lg border-0 text-[10px] font-bold",
                                formData.status === 'Approved' ? "bg-green-100 text-green-700" :
                                formData.status === 'Pending Approval' ? "bg-amber-100 text-amber-700" :
                                "bg-blue-100 text-blue-700"
                            )}>
                                {formData.status}
                            </Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Parts Repaired <span className="text-red-500">*</span></label>
                                <Input
                                    name="partsRepaired"
                                    placeholder="e.g., Brake pads, Engine oil, Screen assembly"
                                    value={formData.partsRepaired}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Description of Repair <span className="text-red-500">*</span></label>
                                <Textarea
                                    name="description"
                                    placeholder="Provide detailed description of the repair work.."
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[100px] p-4 resize-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Vendor <span className="text-red-500">*</span></label>
                                <Input
                                    name="vendor"
                                    placeholder="e.g. AutoTech Repairs"
                                    value={formData.vendor}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Repair Date <span className="text-red-500">*</span></label>
                                <Input
                                    type="date"
                                    name="repairDate"
                                    max="9999-12-31"
                                    value={formData.repairDate}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="space-y-2 relative">
                                <label className="text-xs font-bold text-gray-900">Repair Cost (excluding taxes) <span className="text-red-500">*</span></label>
                                <div className="absolute left-4 top-[38px] text-gray-500 font-medium">$</div>
                                <Input
                                    name="cost"
                                    type="number"
                                    placeholder="0.00"
                                    value={formData.cost}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 pl-8 pr-4"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Approved By <span className="text-red-500">*</span></label>
                                <Select value={formData.approvedBy} onValueChange={(val) => handleSelectChange('approvedBy', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12">
                                        <SelectValue placeholder="Select approver..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100 max-h-[300px]">
                                        {approvers.map(user => (
                                            <SelectItem key={user.id} value={user.id.toString()} className="py-2.5">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-gray-900">{user.name}</span>
                                                    <span className="text-[10px] text-gray-500">{user.jobRole || 'No role'}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Repair Documents */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Camera className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Documents</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Manage photos or PDF documents of the repair work</p>
                            </div>
                        </div>
                        <div className="space-y-6">
                            {/* Existing Documents */}
                            {existingAttachments.length > 0 && (
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Existing Files</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {existingAttachments.map((att) => (
                                            <div key={att.id} className="flex items-center justify-between gap-3 bg-gray-50 p-3 rounded-2xl border border-gray-100 group">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center shrink-0">
                                                        <FileText size={18} className="text-blue-500" />
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <p className="text-xs font-bold text-gray-900 truncate">{att.filename}</p>
                                                        <p className="text-[10px] font-medium text-gray-400">{(att.size / 1024).toFixed(0)} KB</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                        onClick={() => handleRemoveExistingAttachment(att.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* New Uploads */}
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Upload New Files</label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/jpeg,image/png,image/gif,application/pdf"
                                        onChange={(e) => handleFileChange(e)}
                                        className="hidden"
                                        id="image-upload"
                                    />
                                    <label
                                        htmlFor="image-upload"
                                        className="flex items-center w-full min-h-[48px] px-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100 transition-colors cursor-pointer group justify-between"
                                    >
                                        <div className="flex items-center">
                                            <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 text-xs font-bold text-gray-700 mr-3 shadow-sm group-hover:shadow transition-shadow">
                                                Choose Files
                                            </div>
                                            <span className="text-sm font-medium text-gray-500 truncate">
                                                {files.images.length > 0 ? `${files.images.length} new file(s)` : 'No new files chosen'}
                                            </span>
                                        </div>
                                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                            <Upload size={14} className="text-gray-500" />
                                        </div>
                                    </label>
                                </div>
                                {files.images.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {files.images.map((file, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-blue-50 pl-3 pr-2 py-1.5 rounded-lg border border-blue-100">
                                                <Camera size={14} className="text-blue-500" />
                                                <span className="text-xs font-medium text-blue-700 truncate max-w-[150px]">{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeNewImage(i)}
                                                    className="p-1 hover:bg-blue-100 rounded-md transition-colors text-blue-400 hover:text-red-500 ml-1"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Sticky Area */}
                <div className="w-full xl:w-[350px] flex-shrink-0">
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm sticky top-6">
                        <div className="flex items-center gap-3 mb-8">
                            <Settings className="text-gray-400" size={20} />
                            <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Summary</h2>
                        </div>

                        <div className="space-y-6 mb-8">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Asset</span>
                                <p className="text-sm font-medium text-gray-900">{asset.name} ({asset.tag})</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Parts Repaired</span>
                                <p className="text-sm font-medium text-gray-900">{formData.partsRepaired || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Vendor</span>
                                <p className="text-sm font-medium text-gray-900">{formData.vendor || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Repair Cost</span>
                                <p className="text-sm font-medium text-gray-900">{formData.cost ? `$${Number(formData.cost).toFixed(2)}` : 'Not set'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Attachments</span>
                                <p className="text-sm font-medium text-gray-900">{existingAttachments.length + files.images.length} total files</p>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800 text-white h-12 font-bold mb-6 gap-2 text-sm shadow-md transition-all"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Update Repair Record
                                </>
                            )}
                        </Button>

                        <div className="space-y-3">
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Changes will be saved to history</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Approver will be updated if changed</span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            {/* Remove attachment confirmation dialog */}
            <AlertDialog open={attachmentToDelete !== null} onOpenChange={(open) => { if (!open) setAttachmentToDelete(null); }}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Delete Attachment
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this attachment? This action cannot be undone and the file will be removed from the repair record.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={confirmRemoveAttachment}
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default EditRepair;
